// Selectors for MultiWealthica2Store
// Factory returns bound selector functions using the store's `get` accessor.
// Keeping this module free of React/runtime dependencies and avoiding direct imports
// from the main store to prevent circular dependencies.

type UnknownRecord = Record<string, unknown>;
import type { WealthicaAccount } from '@/providers/wealthica2/types';

export type MultiSelectors = {
  getAllAccounts: () => WealthicaAccount[];
  getAllPositions: () => unknown[];
  getTotalAccountValue: () => number;
  getConnectionCount: () => number;
  isAnyConnected: () => boolean;
  isAnyLoadingAccounts: () => boolean;
  getOverallStatus: () => 'connected' | 'disconnected' | 'error';
  getAggregatedAccountsError: () => string | null;
  getAggregatedLoadingState: () => boolean;
  getLastAccountsFetch: () => Date | null;
  getLastSyncTimestamp: () => string | null;
};

export function createSelectors(get: () => unknown): MultiSelectors {
  return {
    // === AGGREGATED DATA SELECTORS ===
    getAllAccounts: (): WealthicaAccount[] => {
      const state = get() as UnknownRecord;
      const connections = (state['connections'] as Map<string, unknown>) || new Map();
      const allAccounts = Array.from(connections.values()).flatMap((conn) => (conn as UnknownRecord)['accounts'] as unknown[] || []);
      // Deduplicate by stable account ID to avoid double counting across duplicate connections
      const seen = new Map<string | number, unknown>();
      const unique: unknown[] = [];
      for (const acc of allAccounts) {
        const accRec = acc as UnknownRecord;
        const rawId = accRec['id'];
        const rawUnderscoreId = accRec['_id'];
        const key = typeof rawId === 'string' || typeof rawId === 'number'
          ? rawId
          : (typeof rawUnderscoreId === 'string' || typeof rawUnderscoreId === 'number' ? rawUnderscoreId : undefined);
        if (key !== undefined) {
          if (!seen.has(key)) {
            seen.set(key, acc);
            unique.push(acc);
          }
        } else {
          // If no ID is present, include as-is (rare)
          unique.push(acc);
        }
      }
      return unique as unknown as WealthicaAccount[];
    },

    getAllPositions: () => {
      const state = get() as UnknownRecord;
      const connections = (state['connections'] as Map<string, unknown>) || new Map();
      const allPositions = Array.from(connections.values()).flatMap((conn) => (conn as UnknownRecord)['positions'] as unknown[] || []);
      // Deduplicate by position ID
      const seen = new Map<string | number, unknown>();
      const unique: unknown[] = [];
      for (const pos of allPositions) {
        const posRec = pos as UnknownRecord;
        const rawId = posRec['id'];
        const rawUnderscoreId = posRec['_id'];
        const key = typeof rawUnderscoreId === 'string' || typeof rawUnderscoreId === 'number'
          ? rawUnderscoreId
          : (typeof rawId === 'string' || typeof rawId === 'number' ? rawId : undefined);
        if (key !== undefined) {
          if (!seen.has(key)) {
            seen.set(key, pos);
            unique.push(pos);
          }
        } else {
          unique.push(pos);
        }
      }
      return unique;
    },

    getTotalAccountValue: (): number => {
      const allAccounts = createSelectors(get).getAllAccounts() as WealthicaAccount[];
      return allAccounts.reduce<number>((total, account: WealthicaAccount) => {
        const bal = account?.balance;
        const balance = typeof bal === 'number' ? bal : parseFloat(String(bal ?? '0'));
        return total + (isNaN(balance) ? 0 : balance);
      }, 0);
    },

    getConnectionCount: () => {
      const state = get() as UnknownRecord;
      const connections = (state['connections'] as Map<string, unknown>) || new Map();
      return connections.size;
    },

    // === UI COMPATIBILITY HELPERS ===
    isAnyConnected: () => {
      const state = get() as UnknownRecord;
      const connections = (state['connections'] as Map<string, unknown>) || new Map();
      for (const conn of connections.values()) {
        const status = (conn as UnknownRecord)['status'];
        if (status === 'connected') return true;
      }
      return false;
    },

    isAnyLoadingAccounts: () => {
      const state = get() as UnknownRecord;
      const isBatch = Boolean(state['isFetchingAllConnections']);
      if (isBatch) return true;
      const connections = (state['connections'] as Map<string, unknown>) || new Map();
      for (const conn of connections.values()) {
        if ((conn as UnknownRecord)['isLoadingAccounts']) return true;
      }
      return false;
    },

    getOverallStatus: () => {
      const state = get() as UnknownRecord;
      const connections = Array.from(((state['connections'] as Map<string, unknown>) || new Map()).values());
      if (connections.length === 0) return 'disconnected';
      const hasError = connections.some((conn) => (conn as UnknownRecord)['status'] === 'error');
      if (hasError) return 'error';
      const hasConnected = connections.some((conn) => (conn as UnknownRecord)['status'] === 'connected');
      return hasConnected ? 'connected' : 'disconnected';
    },

    getAggregatedAccountsError: () => {
      const state = get() as UnknownRecord;
      const connections = Array.from(((state['connections'] as Map<string, unknown>) || new Map()).values());
      const errors = connections.map((conn) => (conn as UnknownRecord)['accountsError']).filter(Boolean) as string[];
      return errors.length > 0 ? errors[0] : null;
    },

    getAggregatedLoadingState: () => {
      const state = get() as UnknownRecord;
      const connections = Array.from(((state['connections'] as Map<string, unknown>) || new Map()).values());
      return connections.some((conn) => Boolean((conn as UnknownRecord)['isLoadingAccounts']));
    },

    getLastAccountsFetch: () => {
      const state = get() as UnknownRecord;
      const connections = Array.from(((state['connections'] as Map<string, unknown>) || new Map()).values());
      const stamps = connections
        .map((conn) => (conn as UnknownRecord)['lastAccountsFetch'] as unknown)
        .filter(Boolean)
        .map((v) => (v instanceof Date ? v : new Date(String(v)))) as Date[];
      stamps.sort((a, b) => b.getTime() - a.getTime());
      return stamps[0] || null;
    },

    getLastSyncTimestamp: () => {
      const state = get() as UnknownRecord;
      const connections = Array.from(((state['connections'] as Map<string, unknown>) || new Map()).values());
      const times = connections
        .map((conn) => ((conn as UnknownRecord)['connectionData'] as UnknownRecord | undefined)?.['lastSyncAt'] as unknown)
        .filter(Boolean)
        .map((v) => (v instanceof Date ? v.toISOString() : String(v))) as string[];
      times.sort((a, b) => new Date(b).getTime() - new Date(a).getTime());
      return times[0] || null;
    }
  };
}
